import React, {Component} from "react";
import { injectIntl } from 'react-intl';
import { notification } from 'antd';
import Conversation from "../../../chat/Conversation";
import connect from "react-redux/es/connect/connect";
import CustomScrollbars from "util/CustomScrollbars";
import TypingArea from "./TypingArea";
import {ConnectionBanner} from "../LiveIndicator/LiveIndicator";
import {
    fetchAccountCurrent,
    fetchCannedResponses,
    fetchOnlyMessages,
    fetchUsers,
    sendMessage,
    turnScrollOnForClientNumber,
    removeErrorMessage,
    markConversationRead
} from "../../../../appRedux/actions";
import CircularProgress from "../../../CircularProgress";
import { buildSenders, conversationSwitches, initialSenderValue, unansweredSwitch, valueOf } from "../../SelectSender/senders";
import SwitchBanner from "./SwitchBanner";
import { draftFromEvent, draftKey, pruneDrafts, readDraft, writeDraft } from "../../../../util/chatDrafts";
import { countryNames } from "../../../../util/countryNames";

class MyCommunication extends Component {
  state = {
    typingAreaContent: '',
    senderNumber: '',
    mmsFile: null,
    error: '',
    dismissedSwitch: null
  };

  componentDidMount() {
    this.props.fetchUsers();
    this.props.fetchAccountCurrent();
    // Saved replies were never loaded, so the list was always empty.
    this.props.fetchCannedResponses();
    if(this.props.selectedConversation) {
        if(this.props.selectedConversation.conversationId) {
          this.props.fetchOnlyMessages(this.props.selectedConversation.conversationId);
        }
        this.props.turnScrollOnForClientNumber(this.props.selectedConversation.clientNumber);
    }
    this.syncSender();
    pruneDrafts();
    window.addEventListener('storage', this.onDraftStorage);
    this.loadDraft();
  }

  componentWillUnmount() {
    this.flushDraft();
    window.removeEventListener('storage', this.onDraftStorage);
  }

  // Drafts: one per conversation, saved in the browser and shared between this operator's tabs (see chatDrafts).
  draftKeyNow = () => {
    const account = this.props.accounts?.accountName;
    return account ? draftKey(account, this.props.selectedConversation?.clientNumber) : null;
  };

  loadDraft = () => {
    const key = this.draftKeyNow();
    const client = this.props.selectedConversation?.clientNumber || null;
    if (key === this.draftLoadedFor) return;
    const sameConversation = client && client === this.draftClient;
    this.flushDraft();
    this.draftLoadedFor = key;
    this.draftClient = client;
    if (sameConversation) {
      // Only the account name arrived: keep what was typed, and save it under the full key.
      if (key && this.state.typingAreaContent) writeDraft(key, this.state.typingAreaContent);
      return;
    }
    const text = readDraft(key);
    if (text !== this.state.typingAreaContent) this.setState({typingAreaContent: text});
  };

  saveDraftSoon = (text) => {
    const key = this.draftLoadedFor;
    clearTimeout(this.draftTimer);
    this.pendingDraft = {key, text};
    this.draftTimer = setTimeout(this.flushDraft, 300);
  };

  flushDraft = () => {
    clearTimeout(this.draftTimer);
    if (this.pendingDraft) {
      writeDraft(this.pendingDraft.key, this.pendingDraft.text);
      this.pendingDraft = null;
    }
  };

  onDraftStorage = (event) => {
    const text = draftFromEvent(event, this.draftLoadedFor);
    if (text === null || text === this.state.typingAreaContent) return;
    // Another tab typed in (or sent) this conversation.
    this.pendingDraft = null;
    clearTimeout(this.draftTimer);
    this.setState({typingAreaContent: text});
  };

  componentDidUpdate() {
      if(this.props.selectedConversation && this.props.selectedConversation.conversationId && !this.props.selectedConversation.isMessagesFetched) {
          this.props.fetchOnlyMessages(this.props.selectedConversation.conversationId);
      }

      this.syncSender();
      this.loadDraft();

      if(this.state.error !== this.props.global.error) {
          this.setState({error: this.props.global.error});

          if(this.props.global.error) {
              notification.open({
                  message: this.props.intl.formatMessage({id: `errorMessage.${this.props.global.error}`, defaultMessage: 'Could not send the message.'}, {countries: countryNames(this.props.intl, this.props.global.errorCountries)}),
                  onClose: this.props.removeErrorMessage()
              });
          }
      }
  }

  // The sender a conversation opens with: one picked when starting it, else the number the customer last wrote
  // to (on that channel), else the one we last replied from, else the default. While it is open, the sender
  // follows the customer when they switch channel or number (the banner above the composer says so). Otherwise
  // the operator's own pick is kept.
  syncSender = () => {
    const conversation = this.props.selectedConversation;
    const numbers = this.props.accounts?.phoneNumbersAll || [];
    if (!conversation?.messages || !numbers.length) return;
    if (!conversation.accountNumber && !conversation.isMessagesFetched) return;
    const switches = conversationSwitches(conversation.messages);
    const lastSwitch = switches[switches.length - 1];
    const next = {client: conversation.clientNumber, account: conversation.accountNumber || '', switchId: lastSwitch?.messageId || ''};
    const prev = this.senderSync || {};
    if (prev.client === next.client && prev.account === next.account && prev.switchId === next.switchId) return;
    this.senderSync = next;

    let value;
    if (prev.client === next.client && prev.account === next.account && lastSwitch) {
      const able = buildSenders(numbers).some(sender => sender.number === lastSwitch.number && sender[lastSwitch.channel]);
      if (!able) return;
      value = valueOf(lastSwitch.number, lastSwitch.channel);
    } else {
      value = conversation.accountNumber || initialSenderValue(conversation.messages, numbers);
    }
    if (value !== this.state.senderNumber) this.setState({senderNumber: value});
  };

  // The customer's latest switch, while we have not replied and the composer still follows it.
  switchToShow = () => {
    const change = unansweredSwitch(this.props.selectedConversation?.messages);
    if (!change || change.messageId === this.state.dismissedSwitch) return null;
    return this.state.senderNumber === valueOf(change.number, change.channel) ? change : null;
  };

  chooseInsteadOfSwitch = (value) => {
    const change = unansweredSwitch(this.props.selectedConversation?.messages);
    this.setState({dismissedSwitch: change?.messageId || null});
    this.updateSenderNumber(value);
  };

  submitComment = () => {
    if (this.state.typingAreaContent === '') {
      return;
    }

    this.markConversationRead();

    const isWhatsappSender = this.state.senderNumber?.endsWith("-whatsapp");
    const senderNumber = isWhatsappSender
          ? this.state.senderNumber.replace("-whatsapp", "")
          : this.state.senderNumber;
    const platform = isWhatsappSender ? "whatsapp" : (this.state.mmsFile ? "mms" : "sms");

    this.props.sendMessage(
        this.state.typingAreaContent,
        this.props.selectedConversation.clientNumber,
        senderNumber,
        platform,
        this.state.mmsFile
    );
    this.updateTypingAreaValue('');
    this.flushDraft();
  };

  updateTypingAreaValue = (text) => {
    this.setState({typingAreaContent: text});
    this.saveDraftSoon(text);
  };

  updateMmsFile = (mmsFile) => {
    this.setState({mmsFile});
  };

  updateSenderNumber = (selectedNumber) => {
    this.markConversationRead();
    this.setState({senderNumber: selectedNumber});
  };

  handleAutoComplete = (value) => {
    const text = this.state.typingAreaContent;
    this.updateTypingAreaValue(text + (text && !/\s$/.test(text) ? ' ' : '') + value);
  };

  focusTypingArea = () => {
    this.markConversationRead();
  };

  markConversationRead = () => {
    if (this.props.selectedConversation.isRead || !this.props.selectedConversation.conversationId) return;
    this.props.markConversationRead(this.props.selectedConversation.conversationId);
  };

  render() {
    if (!this.props.selectedConversation) {
      return '';
    }

    return (
      <div className={"chat-container " + this.props.classList}>
        <div className="gx-chat-main">
            {this.props.selectedConversation.messages ? (
                <>
                  <CustomScrollbars className="gx-chat-list-scroll gx-chat-list-scroll--zoom">
                    <Conversation
                      selectedConversation={this.props.selectedConversation}
                      users={this.props.users}
                    />
                  </CustomScrollbars>

                  {!this.props.isArchive && !this.props.isTrialExpired && <ConnectionBanner/>}
                  {!this.props.isArchive && !this.props.isTrialExpired &&
                    <SwitchBanner
                      change={this.switchToShow()}
                      messages={this.props.selectedConversation.messages}
                      phoneNumbers={this.props.accounts.phoneNumbersAll}
                      onChoose={this.chooseInsteadOfSwitch}
                    />
                  }
                  {!this.props.isArchive && !this.props.isTrialExpired &&
                    <TypingArea
                      message={this.state.typingAreaContent}
                      updateTypingAreaValue={this.updateTypingAreaValue}
                      updateMmsFile={this.updateMmsFile}
                      focusTypingArea={this.focusTypingArea}
                      submitComment={this.submitComment}
                      isArchive={this.props.isArchive}
                      handleAutoComplete={this.handleAutoComplete}
                      cannedResponses={this.props.cannedResponses}
                      onCannedSaved={this.props.fetchCannedResponses}
                      senderNumber={this.state.senderNumber}
                      phoneNumbersAll={this.props.accounts.phoneNumbersAll}
                      updateSenderNumber={this.updateSenderNumber}
                      clientNumber={this.props.selectedConversation.clientNumber}
                      conversationMessages={this.props.selectedConversation.messages}
                    />
                  }
                </>
            ) : (
                <div className="gx-loader-view">
                    <CircularProgress/>
                </div>
            )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  global: state.global,
  conversations: state.messages,
  users: state.users,
  cannedResponses: state.cannedResponses,
  accounts: state.accounts
});

export default injectIntl(connect(mapStateToProps, {
  fetchUsers,
  fetchCannedResponses,
  fetchAccountCurrent,
  sendMessage,
  fetchOnlyMessages,
  turnScrollOnForClientNumber,
  removeErrorMessage,
  markConversationRead
})(MyCommunication));
